import { CheckCircle } from '@untitledui/icons';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { CloseButton } from '@/lib/base/buttons/close-button';
import { navigateToConversation } from '@/lib/navigation/appNavigate';
import { cx } from '@/utils/cx';

import {
  CHAT_TOAST_POSITION,
  CHAT_TOAST_WRAPPER_STYLE,
} from './chatRunToastStyles';

const TOAST_OPTIONS = {
  duration: 5000,
  position: CHAT_TOAST_POSITION,
  style: CHAT_TOAST_WRAPPER_STYLE,
};

/**
 * Show a success toast when a main-chat run finishes while the user is away
 * from the conversation. Transient — auto-dismisses after 5s since the user
 * can always find the finished chat in the conversation list.
 */
export function showChatRunCompleteToast({
  orgId,
  conversationId,
}: {
  orgId: string;
  conversationId: string;
}): void {
  toast.custom(
    t => (
      <div
        role='status'
        aria-live='polite'
        aria-atomic='true'
        className={cx(
          'flex items-start gap-3 p-3.5 bg-bg-primary border border-border-secondary rounded-xl shadow-md min-w-[320px] max-w-[420px]'
        )}
      >
        <div className='flex items-center justify-center size-9 rounded-full bg-success-50 shrink-0 mt-0.5'>
          <CheckCircle className='size-5 text-success-600' aria-hidden='true' />
        </div>

        <div className='flex-1 min-w-0 space-y-1'>
          <p className='text-sm font-semibold text-fg-primary'>Chat ready</p>
          <p className='text-sm text-fg-secondary'>
            Your chat finished — ready for your review.
          </p>
          <div className='pt-1'>
            <Button
              size='sm'
              color='secondary'
              onClick={() => {
                // In-app SPA navigation — a hard reload here would discard
                // client state and violate the epic's no-reload success
                // metric (asserted by navigation-resume.spec.ts).
                navigateToConversation(orgId, conversationId);
                toast.dismiss(t);
              }}
            >
              View
            </Button>
          </div>
        </div>

        <CloseButton
          size='xs'
          label='Dismiss'
          onPress={() => toast.dismiss(t)}
          className='shrink-0'
        />
      </div>
    ),
    TOAST_OPTIONS
  );
}
